<template>
  <v-navigation-drawer
    v-model="_internalValue"
    temporary
    absolute
    right
    v-bind="$attrs"
    v-on="$listeners"
    @input="emitInput"
    width="400px"
  >
    <v-card :elevation="0">
      <v-card-title>
        <slot name="title">
          <div class="d-flex flex-row justify-space-between">
            <div class="d-flex flex-row align-center">
              <v-icon class="mr-2" color="primary" @click="close">
                mdi-chevron-left
              </v-icon>

              <span class="text-h6">Filtros</span>
            </div>
          </div>
        </slot>
      </v-card-title>

      <v-card-text>
        <slot>
          <v-form @submit.prevent="submit">
            <slot name="content">
              <div class="d-flex flex-column pa-2">
                <ItemSelector
                  class="mt-2"
                  v-if="_isAdmin"
                  v-model="selectors.groups.selected"
                  :items="selectors.groups.items"
                  :label="selectors.groups.label"
                  :placeholder="selectors.groups.placeholder"
                  :count="selectors.groups.count"
                  :multiple="selectors.groups.multiple"
                  :clearable="selectors.groups.clearable"
                  :item-value="selectors.groups.itemValue"
                  :item-text="selectors.groups.itemText"
                  :allIfEmpty="true"
                  @search="selectors.groups.onSearch"
                  @paginate="selectors.groups.onPaginate"
                />

                <ItemSelector
                  class="mt-2"
                  v-if="_isAdmin || _isModerator"
                  v-model="selectors.subgroups.selected"
                  :items="selectors.subgroups.items"
                  :label="selectors.subgroups.label"
                  :placeholder="selectors.subgroups.placeholder"
                  :count="selectors.subgroups.count"
                  :multiple="selectors.subgroups.multiple"
                  :clearable="selectors.subgroups.clearable"
                  :item-value="selectors.subgroups.itemValue"
                  :item-text="selectors.subgroups.itemText"
                  :disabled="
                    !_isModerator && !selectors.groups.selected?.length
                  "
                  :allIfEmpty="true"
                  @search="selectors.subgroups.onSearch"
                  @paginate="selectors.subgroups.onPaginate"
                />

                <ItemSelector
                  v-if="_cabinetMode"
                  class="mt-2"
                  v-model="selectors.cabinets.selected"
                  :items="selectors.cabinets.items"
                  :label="selectors.cabinets.label"
                  :placeholder="selectors.cabinets.placeholder"
                  :count="selectors.cabinets.count"
                  :multiple="selectors.cabinets.multiple"
                  :clearable="selectors.cabinets.clearable"
                  :item-value="selectors.cabinets.itemValue"
                  :item-text="selectors.cabinets.itemText"
                  :disabled="
                    !_isOperator &&
                    !_isSupervisor &&
                    !selectors.subgroups.selected?.length
                  "
                  :allIfEmpty="true"
                  @search="selectors.cabinets.onSearch"
                  @paginate="selectors.cabinets.onPaginate"
                />

                <ItemSelector
                  v-else
                  class="mt-2"
                  v-model="selectors.desks.selected"
                  :items="selectors.desks.items"
                  :label="selectors.desks.label"
                  :placeholder="selectors.desks.placeholder"
                  :count="selectors.desks.count"
                  :multiple="selectors.desks.multiple"
                  :clearable="selectors.desks.clearable"
                  :item-value="selectors.desks.itemValue"
                  :item-text="selectors.desks.itemText"
                  :disabled="
                    !_isOperator &&
                    !_isSupervisor &&
                    !selectors.subgroups.selected?.length
                  "
                  :allIfEmpty="true"
                  @search="selectors.desks.onSearch"
                  @paginate="selectors.desks.onPaginate"
                />

                <ItemSelector
                  v-if="
                    (_isAdmin || _isModerator || _isSupervisor) &&
                    !hideFilters.includes('attendant')
                  "
                  v-model="selectors.attendant.selected"
                  class="mt-2"
                  :items="selectors.attendant.items"
                  :label="
                    _cabinetMode
                      ? 'Assessor / Magistrado'
                      : selectors.attendant.label
                  "
                  :placeholder="selectors.attendant.placeholder"
                  :count="selectors.attendant.count"
                  :multiple="selectors.attendant.multiple"
                  :clearable="selectors.attendant.clearable"
                  :item-value="selectors.attendant.itemValue"
                  :item-text="selectors.attendant.itemText"
                  :itemSubText="selectors.attendant.itemSubtext"
                  :allIfEmpty="true"
                  @search="selectors.attendant.onSearch"
                  @paginate="selectors.attendant.onPaginate"
                />
              </div>
            </slot>
          </v-form>
        </slot>
      </v-card-text>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import ItemSelector from "../itemSelector/ItemSelector.vue";
import { getJurisdictions } from "@/services/jurisdictions";
import { getCourts } from "@/services/courts";
import { getCounters } from "@/services/counters";
import { getUsers } from "@/services/users";
import { getPermissionGroups } from "@/services/permissions";
import { handleAlert } from "@/utils";

export default {
  name: "FilterDrawer",

  components: {
    ItemSelector,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    hideFilters: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    ...mapState(["user", "filters"]),
    ...mapGetters(["attendanceRole"]),

    _internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    _isOperator() {
      return this.attendanceRole === "operator";
    },
    _isModerator() {
      return this.attendanceRole === "moderator";
    },
    _isAdmin() {
      return this.attendanceRole === "admin";
    },
    _isSupervisor() {
      return this.attendanceRole === "supervisor";
    },

    _cabinetMode() {
      const routes = [
        "SchedulesReports",
        "ScheduleQueueAttendance",
        "ScheduleTimeAttendance",
        "ScheduleSatisfaction",
      ];

      return !!routes.includes(this.$route.name);
    },
  },

  watch: {
    hideFilters(val) {
      if (val) {
        if (val.includes("attendant")) {
          this.selectors.attendant.selected = [];

          this.setFiltersAttendants([]);
        }
      }
    },

    "selectors.groups.selected": {
      handler(value) {
        // console.log("updated:groups");
        this.setFiltersGroups(value);
        this.selectors.subgroups.page = 0;
        this.selectors.subgroups.count = 0;
        this.selectors.subgroups.items = [];
        this.selectors.subgroups.selected = [];

        if (value?.length) {
          this.handleFetchSubgroups();
        }
      },
      deep: true,
    },
    "selectors.subgroups.selected": {
      handler(value) {
        // console.log("updated:subgroups");
        this.setFiltersSubgroups(value);
        this.selectors.desks.page = 0;
        this.selectors.desks.count = 0;
        this.selectors.desks.items = [];
        this.selectors.desks.selected = [];

        if (value?.length) {
          if (this._cabinetMode) this.handleFetchCabinets();
          else this.handleFetchDesks();
        }
      },
      deep: true,
    },
    "selectors.desks.selected": {
      handler(value) {
        this.setFiltersDesks(value);
      },
      deep: true,
    },
    "selectors.cabinets.selected": {
      handler(value) {
        this.setFiltersDesks(value);
      },
      deep: true,
    },
    "selectors.attendant.selected": {
      handler(value) {
        // console.log("updated:attendant");
        if (Array.isArray(value)) return this.setFiltersAttendants(value);
        this.setFiltersAttendants(value ? [value] : []);
      },
      deep: true,
    },
    _cabinetMode() {
      this.selectors.attendant.selected = [];
      this.selectors.attendant.items = [];

      this.fetchAttendants();
    },
  },

  data() {
    return {
      attendanceGroups: [],
      selectors: {
        groups: {
          label: this.$t("groups"),
          placeholder: `Digite para buscar ${this.$t("groups")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: [],
          loading: false,
          multiple: true,
          clearable: true,
          count: 0,
          page: 0,
          onSearch: this.onSearchGroups,
          onPaginate: this.fetchMoreGroups,
        },
        subgroups: {
          label: this.$t("subgroups"),
          placeholder: `Digite para buscar ${this.$t("subgroups")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: [],
          loading: false,
          multiple: true,
          clearable: true,
          count: 0,
          page: 0,
          onSearch: this.onSearchSubgroups,
          onPaginate: this.fetchMoreSubgroups,
        },
        desks: {
          label: this.$t("desks"),
          placeholder: `Digite para buscar ${this.$t("desks")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: [],
          loading: false,
          multiple: true,
          clearable: true,
          count: 0,
          page: 0,
          onSearch: this.onSearchDesks,
          onPaginate: this.fetchMoreDesks,
        },
        cabinets: {
          label: this.$t("cabinets"),
          placeholder: `Digite para buscar ${this.$t("cabinets")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: [],
          loading: false,
          multiple: true,
          clearable: true,
          count: 0,
          page: 0,
          onSearch: this.onSearchCabinets,
          onPaginate: this.fetchMoreCabinets,
        },
        attendant: {
          label: "Atendente",
          placeholder: "Digite para buscar Atendentes...",
          itemValue: "_id",
          itemText: "name",
          itemSubtext: "email",
          items: [],
          selected: [],
          loading: false,
          multiple: false,
          clearable: true,
          count: 0,
          page: 0,
          onSearch: this.onSearchAttendants,
          onPaginate: this.fetchMoreAttendants,
        },
      },
    };
  },

  beforeMount() {
    if (this.filters?.desks?.length) {
      this.selectors.desks.selected = this.filters.desks;
      this.selectors.desks.items = this.filters.desks;
    }

    if (this.filters?.subgroups?.length) {
      this.selectors.subgroups.selected = this.filters.subgroups;
      this.selectors.subgroups.items = this.filters.subgroups;
    }

    if (this.filters?.groups?.length) {
      this.selectors.groups.selected = this.filters.groups;
      this.selectors.groups.items = this.filters.groups;
    }

    if (this.filters?.attendants?.length) {
      this.selectors.attendant.selected = this.filters.attendants[0];
      this.selectors.attendant.items = this.filters.attendants;
    }
  },

  mounted() {
    if (this._isAdmin || this._isModerator || this._isSupervisor) {
      this.fetchAttendants();
    }

    if (this._isAdmin) {
      this.handleFetchGroups();
    }

    if (this._isModerator) {
      this.handleFetchSubgroups();
    }

    if (this._isOperator || this._isSupervisor) {
      if (this._cabinetMode) this.handleFetchCabinets();
      else this.handleFetchDesks();
    }
  },

  methods: {
    ...mapActions([
      "setFiltersGroups",
      "setFiltersSubgroups",
      "setFiltersDesks",
      "setFiltersAttendants",
    ]),
    submit() {
      this.$emit("submit");
    },
    emitInput(value) {
      this.$emit("input", value);
    },
    close() {
      this._internalValue = false;
    },

    async fetchAttendants() {
      await this.getAttendancePermissionGroups();
      this.handleFetchAttendants();
    },

    async handleFetchGroups(search = "", increment = false) {
      if (!this._isAdmin) {
        return;
      }

      this.selectors.groups.loading = true;

      try {
        const groupIDs = this.selectors.groups.selected.map((item) => item._id);

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.groups.page * 10,
          personID: this.user._id,
          ...(!!groupIDs?.length && { nin: groupIDs }),
          ...(!!search?.length && { search }),
        };
        const {
          data,
          headers: { "x-count": count },
        } = await getJurisdictions(payload);

        this.selectors.groups.count = Number(count);

        if (increment) {
          this.selectors.groups.items.push(...data);
        } else {
          this.selectors.groups.items = data;
        }
      } catch (error) {
        console.log("Falha ao obter grupos", error);
        this.handleAlert(
          error?.data?.message || "Falha ao obter grupos",
          "error"
        );
      } finally {
        this.selectors.groups.loading = false;
      }
    },

    async handleFetchSubgroups(search = "", increment = false) {
      if (!this._isAdmin && !this._isModerator) {
        return;
      }

      this.selectors.subgroups.loading = true;

      try {
        const groupIDs = this.selectors.groups.selected?.map((s) => s._id);
        const subgroupIDs = this.selectors.subgroups.selected?.map(
          (s) => s._id
        );

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.subgroups.page * 10,
          ...(subgroupIDs?.length && { nin: subgroupIDs }),
          ...(this._isModerator && {
            personID: this.user._id,
            directOnly: true,
          }),
          ...(!!groupIDs?.length && { groupIDs }),
          ...(!!search?.length && { search }),
        };
        const {
          data,
          headers: { "x-count": count },
        } = await getCourts(payload);

        this.selectors.subgroups.count = Number(count);

        if (increment) {
          this.selectors.subgroups.items.push(...data);
        } else {
          this.selectors.subgroups.items = data;
        }
      } catch (error) {
        console.log("Falha ao obter subgrupos", error);
        this.handleAlert(
          error?.data?.message || "Falha ao obter subgrupos",
          "error"
        );
      } finally {
        this.selectors.subgroups.loading = false;
      }
    },

    async handleFetchDesks(search = "", increment = false) {
      if (
        !this._isOperator &&
        !this._isSupervisor &&
        !this.selectors.subgroups.selected?.length
      ) {
        return;
      }

      this.selectors.desks.loading = true;

      try {
        const subgroupIDs = this.selectors.subgroups.selected?.map(
          (s) => s._id
        );
        const deskIDs = this.selectors.desks.selected?.map((s) => s._id);

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.desks.page * 10,
          ...(deskIDs?.length && { nin: deskIDs }),
          ...((this._isOperator || this._isSupervisor) && {
            personID: this.user._id,
            directOnly: true,
          }),
          ...(!!search?.length && { search }),
          ...(!!subgroupIDs?.length && { subgroupIDs }),
        };

        const {
          data,
          headers: { "x-count": count },
        } = await getCounters(payload);

        this.selectors.desks.count = Number(count);

        if (increment) {
          this.selectors.desks.items.push(...data);
        } else {
          this.selectors.desks.items = data;
        }
      } catch (error) {
        this.handleAlert(
          error?.data?.message || `Falha ao obter ${this.$t("desks")}`,
          "error"
        );
      } finally {
        this.selectors.desks.loading = false;
      }
    },

    async handleFetchCabinets(search = "", increment = false) {
      if (
        !this._isOperator &&
        !this._isSupervisor &&
        !this.selectors.subgroups.selected?.length
      ) {
        return;
      }

      this.selectors.cabinets.loading = true;

      try {
        const subgroupIDs = this.selectors.subgroups.selected?.map(
          (s) => s._id
        );
        const deskIDs = this.selectors.cabinets.selected?.map((s) => s._id);

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          counterType: "cabinet",
          isActive: true,
          limit: 10,
          offset: this.selectors.cabinets.page * 10,
          ...(deskIDs?.length && { nin: deskIDs }),
          ...((this._isOperator || this._isSupervisor) && {
            personID: this.user._id,
            directOnly: true,
          }),
          ...(!!search?.length && { search }),
          ...(!!subgroupIDs?.length && { subgroupIDs }),
        };

        const {
          data,
          headers: { "x-count": count },
        } = await getCounters(payload);

        this.selectors.cabinets.count = Number(count);

        if (increment) {
          this.selectors.cabinets.items.push(...data);
        } else {
          this.selectors.cabinets.items = data;
        }
      } catch (error) {
        this.handleAlert(
          error?.data?.message || `Falha ao obter ${this.$t("cabinets")}`,
          "error"
        );
      } finally {
        this.selectors.cabinets.loading = false;
      }
    },

    async handleFetchAttendants(search = "", increment = false) {
      if (!this.$can("people")) return;

      this.selectors.attendant.loading = true;

      try {
        const permissionGroups = this._cabinetMode
          ? this.attendanceGroups.filter(
              (el) =>
                (el.role === "supervisor" || el.role === "operator") &&
                (el.permissions || []).find(
                  (perm) =>
                    perm.module === "appointments" &&
                    perm.permissions.includes("appointments.queue")
                )
            )
          : [];

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.attendant.page * 10,
          ...(permissionGroups.length && {
            permissionGroupIDs: permissionGroups.map((el) => el._id),
          }),
          ...(!!search?.length && { search }),
        };

        const {
          data,
          headers: { "x-count": count },
        } = await getUsers(payload);

        this.selectors.attendant.count = Number(count);

        if (increment) {
          this.selectors.attendant.items.push(...data);
        } else {
          this.selectors.attendant.items = data;
        }
      } catch (error) {
        console.log(error);
        this.handleAlert(
          error?.data?.message || `Falha ao obter atendentes`,
          "error"
        );
      } finally {
        this.selectors.attendant.loading = false;
      }
    },

    async getAttendancePermissionGroups() {
      try {
        const payload = {
          module: "attendances",
          type: "role",
          sortBy: "level",
          sortOrder: "desc",
        };

        const { data } = await getPermissionGroups(payload);

        this.attendanceGroups = data;
      } catch (error) {
        this.handleAlert(error.data.message, "error");
      }
    },

    onSearchGroups(search) {
      this.selectors.groups.page = 0;
      this.selectors.groups.count = 0;
      this.selectors.groups.items = [];

      this.handleFetchGroups(search);
    },

    onSearchSubgroups(search) {
      this.selectors.subgroups.page = 0;
      this.selectors.subgroups.count = 0;
      this.selectors.subgroups.items = [];

      this.handleFetchSubgroups(search);
    },

    onSearchDesks(search) {
      this.selectors.desks.page = 0;
      this.selectors.desks.count = 0;
      this.selectors.desks.items = [];

      this.handleFetchDesks(search);
    },

    onSearchCabinets(search) {
      this.selectors.cabinets.page = 0;
      this.selectors.cabinets.count = 0;
      this.selectors.cabinets.items = [];

      this.handleFetchCabinets(search);
    },

    onSearchAttendants(search) {
      this.selectors.attendant.page = 0;
      this.selectors.attendant.count = 0;
      this.selectors.attendant.items = [];

      this.handleFetchAttendants(search);
    },

    fetchMoreGroups(search) {
      this.selectors.groups.page++;
      this.handleFetchGroups(search, true);
    },

    fetchMoreSubgroups(search) {
      this.selectors.subgroups.page++;
      this.handleFetchSubgroups(search, true);
    },

    fetchMoreDesks(search) {
      this.selectors.desks.page++;
      this.handleFetchDesks(search, true);
    },

    fetchMoreCabinets(search) {
      this.selectors.cabinets.page++;
      this.handleFetchCabinets(search, true);
    },

    fetchMoreAttendants(search) {
      this.selectors.attendant.page++;
      this.handleFetchAttendants(search, true);
    },

    handleAlert,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
